import { isNil, reject, some } from "lodash";
import { useMemo } from "react";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";

/**
 * Checks if a dimension has scores. Only checks the sub dimensions, not the parent dimensions.
 * @param dimensions
 * @returns boolean
 */
const checkHasDimensionScores = (dimensions?: DimensionWithScores[]) => {
  const hasScores = some(dimensions, (d) => !!d.score?.value);
  const subDimensions = reject(dimensions, (d) => d.parentId === null);
  return hasScores && !some(subDimensions, (d) => isNil(d.score?.value));
};

export function useProfileCompletion(dimensionsData: {
  personality?: DimensionWithScores[];
  competencies?: DimensionWithScores[];
  values?: DimensionWithScores[];
}) {
  const isRraGptFlow = import.meta.env.VITE_GPT_FLOW_CONFIG === "rraqa";

  if (isRraGptFlow) {
    return {
      personalityComplete: false,
      competenciesComplete: false,
      valuesComplete: false,
      allDimensionsComplete: false,
    };
  }

  return useMemo(() => {
    const personalityComplete = checkHasDimensionScores(
      dimensionsData?.personality
    );

    const competenciesComplete = checkHasDimensionScores(
      dimensionsData?.competencies
    );

    const valuesComplete = checkHasDimensionScores(dimensionsData?.values);

    return {
      personalityComplete,
      competenciesComplete,
      valuesComplete,
      allDimensionsComplete:
        personalityComplete && competenciesComplete && valuesComplete,
    };
  }, [
    dimensionsData?.personality,
    dimensionsData?.competencies,
    dimensionsData?.values,
  ]);
}
